import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import debounce from 'lodash/debounce';

import Box from '@quintoandar/cozy-core/Box';
import Button from '@quintoandar/cozy-core/Button';
import WarningIcon from '@quintoandar/cozy-icons/Warning';

import DynamicForm from 'components/DynamicForm';
import { IconDescription } from 'components/IconDescription';

import {
  checklistItemChanged,
  updateStorageChecklistItem,
  finishChecklistSessionAction,
} from './actions';

import Steps from './Steps';
import { SECTIONS_TYPE, DEBOUNCE_STORAGE_UPDATE_WAIT_TIME } from './constants';
import OverviewNotes from './OverviewNotes';
import messages from './messages';

export const buildHouseTypeSection = ({ house }) => ({
  house: {
    isHeading: true,
    label: house.label,
    items: {
      tipo: house.items.tipo,
    },
  },
});

const debounceInputUpdate = debounce((dispatch, updateParams) => {
  dispatch(updateStorageChecklistItem(updateParams));
}, DEBOUNCE_STORAGE_UPDATE_WAIT_TIME);

export const OverviewForm = ({
  houseId,
  onChangeItemChecklist,
  onChangeNotes,
  fixedSection,
  photoSessionId,
  userId,
  isValid,
  onFinishSessionClick,
  isSectionsFinished,
  progress,
  shouldNotShowComponents,
  finishChecklistSession,
  checklist,
}) => {
  let shouldRenderHouseType = false;
  let shouldRenderPhotoSessionNotes = false;

  const handleFinishChecklist = useCallback(() => {
    finishChecklistSession(photoSessionId, checklist);
  });

  const shouldFinish =
    progress.total !== 0 &&
    progress.total === progress.current &&
    checklist?.fixed.house.items.tipo &&
    checklist?.fixed.house.items.tipo.value !== undefined;

  if (fixedSection.house.items) {
    if (fixedSection.house.items.tipo !== undefined) {
      shouldRenderHouseType = true;
    }

    if (fixedSection.photoSession.items.photoSessionNotes) {
      shouldRenderPhotoSessionNotes = true;
    }
  }

  return (
    <Box>
      {shouldRenderHouseType ? (
        <DynamicForm
          onChange={onChangeItemChecklist({
            sectionType: SECTIONS_TYPE.FIXED,
            userId,
          })}
          sections={buildHouseTypeSection({ house: fixedSection.house })}
        />
      ) : null}

      <Box mt={4}>
        <Steps
          houseId={houseId}
          photoSessionId={photoSessionId}
          progress={progress}
          isSectionsFinished={isSectionsFinished}
          isNewChecklist={shouldNotShowComponents}
        />
      </Box>

      {shouldRenderPhotoSessionNotes ? (
        <OverviewNotes
          section={fixedSection.photoSession}
          onChangeNotes={onChangeNotes({
            sectionType: SECTIONS_TYPE.FIXED,
            userId,
          })}
          onBlurNotes={shouldFinish ? handleFinishChecklist : null}
        />
      ) : null}

      {!shouldNotShowComponents && (
        <Box pb={2}>
          {/* istanbul ignore next */
            !isValid ? (
              <IconDescription
                color="error"
                label={messages.invalid}
                icon={WarningIcon}
              />
            ) : null}
          <Button fullWidth onClick={onFinishSessionClick}>
            <FormattedMessage {...messages.finishChecklist} />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export const mapDispatchToProps = (dispatch, ownProps) => ({
  onChangeItemChecklist: ({ sectionType, userId }) => ({
    section,
    itemSlug,
    value,
    previousValue,
  }) => {
    dispatch(
      checklistItemChanged({
        sectionType,
        section,
        itemSlug,
        value,
        previousValue,
        photoSessionId: ownProps.photoSessionId,
      }),
    );
    dispatch(
      updateStorageChecklistItem({
        sectionType,
        section,
        itemSlug,
        value,
        previousValue,
        photoSessionId: ownProps.photoSessionId,
        userId,
      }),
    );
  },
  onChangeNotes: ({ sectionType, userId }) => ({
    section,
    itemSlug,
    value,
    previousValue,
  }) => {
    dispatch(
      checklistItemChanged({
        sectionType,
        section,
        itemSlug,
        value,
        previousValue,
        photoSessionId: ownProps.photoSessionId,
      }),
    );
    debounceInputUpdate(dispatch, {
      sectionType,
      section,
      itemSlug,
      value,
      previousValue,
      photoSessionId: ownProps.photoSessionId,
      userId,
    });
  },
  finishChecklistSession: (photoSessionId, checklist) =>
    dispatch(finishChecklistSessionAction({ photoSessionId, checklist })),
});

OverviewForm.propTypes = {
  userId: PropTypes.number.isRequired,
  isValid: PropTypes.bool.isRequired,
  onFinishSessionClick: PropTypes.func.isRequired,
  isSectionsFinished: PropTypes.bool.isRequired,
  progress: PropTypes.number.isRequired,
  houseId: PropTypes.number,
  onChangeNotes: PropTypes.func.isRequired,
  fixedSection: PropTypes.shape({
    house: PropTypes.object,
    photoSession: PropTypes.object,
  }),
  onChangeItemChecklist: PropTypes.func.isRequired,
  photoSessionId: PropTypes.number,
  shouldNotShowComponents: PropTypes.bool,
  finishChecklistSession: PropTypes.func,
  checklist: PropTypes.object,
};

export default connect(
  null,
  mapDispatchToProps,
)(OverviewForm);
